import { useEffect } from "react";
import { siteConfig } from "@/lib/site-config";

interface SeoProps {
  title: string;
  description: string;
  path: string;
  ogType?: "website" | "article";
  ogImage?: string;
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  noindex?: boolean;
}

/**
 * Head manager. react-helmet-async does not inject tags reliably on this
 * hosting stack (Helmet output never reaches the DOM), so we manage the
 * document head directly via the DOM. This guarantees per-route title,
 * description, canonical, Open Graph / Twitter tags and JSON-LD.
 */
function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

const Seo = ({ title, description, path, ogType = "website", ogImage, jsonLd, noindex = false }: SeoProps) => {
  const url = `${siteConfig.url}${path}`;
  const image = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `${siteConfig.url}${ogImage}`
    : `${siteConfig.url}${siteConfig.ogImage}`;

  const ldArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];
  const ldString = JSON.stringify(ldArray);

  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "robots", noindex ? "noindex, follow" : "index, follow");
    upsertLink("canonical", url);

    upsertMeta("property", "og:type", ogType);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    // Replace any previously-injected route JSON-LD (leaves the static
    // LocalBusiness schema in index.html untouched).
    document.head.querySelectorAll('script[data-seo-jsonld="true"]').forEach((n) => n.remove());
    for (const ld of JSON.parse(ldString) as Record<string, unknown>[]) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-seo-jsonld", "true");
      s.textContent = JSON.stringify(ld);
      document.head.appendChild(s);
    }
  }, [title, description, url, ogType, image, noindex, ldString]);

  return null;
};

export default Seo;
